<template>
  <div class="teilnehmer-vorgang-card">
    <div class="teilnehmer-vorgang-card__header" @click="toggleExpanded">
      <div class="teilnehmer-vorgang-card__header-group">
        <div class="teilnehmer-vorgang-card__header-item">
          <ChevronDown v-if="expanded" :size="18" class="mr-2 flex-shrink-0" />
          <ChevronRight v-else :size="18" class="mr-2 flex-shrink-0" />
        </div>
        <div class="teilnehmer-vorgang-card__header-item teilnehmer-vorgang-card__header-item--bnr">
          <Hash :size="16" class="mr-4" style="color: #3699ff" />
          <span class="teilnehmer-vorgang-card__header-label">BNR</span>
          <span class="teilnehmer-vorgang-card__header-value">{{ vorgang.id }}</span>
          <b-badge v-if="vorgang.blankState === 'StornierteBuchung'" variant="danger" class="ml-2">
            Storniert
          </b-badge>
        </div>
      </div>

      <div class="teilnehmer-vorgang-card__header-item">
        <Calendar :size="16" class="mr-4" style="color: #3699ff" />
        <span class="teilnehmer-vorgang-card__header-label">Reisezeitraum</span>
        <span class="teilnehmer-vorgang-card__header-value">
          {{ vorgang.reisedatum | date }} - {{ vorgang.enddatum | date }}
        </span>
      </div>

      <div class="teilnehmer-vorgang-card__header-item">
        <Users :size="16" class="mr-4" style="color: #3699ff" />
        <span class="teilnehmer-vorgang-card__header-label">PAX</span>
        <span class="teilnehmer-vorgang-card__header-value">{{ vorgang.npx }}</span>
      </div>

      <div
        v-if="showReleasepruefung"
        class="teilnehmer-vorgang-card__header-item teilnehmer-vorgang-card__header-item--release"
      >
        <span class="teilnehmer-vorgang-card__header-label">Releaseprüfung</span>
        <VorgangReleaseprocessProgressBar
          :vorgang="vorgang"
          :isReiseOhneFlug="isReiseOhneFlug"
        ></VorgangReleaseprocessProgressBar>
        <button
          v-if="showGeprueftCheckbox"
          type="button"
          role="checkbox"
          :aria-checked="istGeprueft"
          class="teilnehmer-vorgang-card__geprueft ml-auto"
          :class="{ 'teilnehmer-vorgang-card__geprueft--checked': istGeprueft }"
          :disabled="isGeprueftDisabled"
          v-b-tooltip.hover
          title="Passdaten &amp; Dokumente: geprüft, vorhanden"
          @click.stop="$emit('geprueft-changed', !istGeprueft)"
        >
          <span class="teilnehmer-vorgang-card__geprueft-label">Geprüft</span>
          <span
            class="teilnehmer-vorgang-card__geprueft-box"
            :class="{ 'teilnehmer-vorgang-card__geprueft-box--checked': istGeprueft }"
          >
            <Check v-if="istGeprueft" :size="12" :stroke-width="3" />
          </span>
        </button>
      </div>
    </div>

    <div v-if="expanded" class="teilnehmer-vorgang-card__body">
      <div v-if="teilnehmerListe.length === 0" class="text-muted p-4">Keine Teilnehmer vorhanden</div>
      <div v-else class="d-flex">
        <div class="teilnehmer-vorgang-card__sidebar">
          <TeilnehmerPersonList
            :teilnehmerListe="teilnehmerListe"
            :selectedIndex="selectedTeilnehmerIndex"
            :kunde="vorgang.kunde"
            :agentur="vorgang.agentur"
            :konfiguration="konfiguration"
            :reiseterminStartdatum="reiseterminStartdatum"
            :reiseterminEnddatum="reiseterminEnddatum"
            :dokumenteByTeilnehmer="dokumenteByTeilnehmer"
            :verlaufEnabled="verlaufEnabled"
            @select="onSelectTeilnehmer"
            @show-verlauf="onShowVerlauf"
          ></TeilnehmerPersonList>
        </div>
        <div class="teilnehmer-vorgang-card__detail flex-grow-1" style="min-width: 0">
          <TeilnehmerVerlauf
            v-if="verlaufEnabled && showVerlauf && selectedTeilnehmer"
            :vorgangId="vorgang.id"
            :teilnehmer="selectedTeilnehmer"
            @close="showVerlauf = false"
          ></TeilnehmerVerlauf>
          <TeilnehmerDetail
            v-else-if="selectedTeilnehmer"
            :key="selectedTeilnehmer.nummer"
            :teilnehmer="selectedTeilnehmer"
            :vorgangId="vorgang.id"
            :konfiguration="konfiguration"
            :reiseterminStartdatum="reiseterminStartdatum"
            :isEditAllowed="isEditAllowed"
            :dokumente="dokumenteByTeilnehmer[selectedTeilnehmer.nummer] || []"
            :downloadAllowed="downloadAllowed"
            :previewAllowed="downloadAllowed"
            :saveHandler="saveHandler"
            @dirty-changed="hasUnsavedChanges = $event"
            @dokument-uploaded="dokument => $emit('dokument-uploaded', dokument)"
            @dokument-deleted="dokument => $emit('dokument-deleted', dokument)"
          ></TeilnehmerDetail>
        </div>
      </div>
    </div>

    <!-- Bestätigungsdialog beim Verlassen der Detailansicht mit offenem Entwurf -->
    <DeleteConfirmationModal
      :id="verwerfenModalId"
      title="Änderungen verwerfen?"
      confirmTitle="Verwerfen"
      :show-irreversible-hint="false"
      @confirm="onVerwerfenConfirm"
      @cancel="onVerwerfenCancel"
      @hidden="aufloesen(false)"
    >
      Es gibt nicht gespeicherte Änderungen an den Teilnehmerdaten. Sollen diese verworfen werden?
    </DeleteConfirmationModal>
  </div>
</template>

<script>
import VorgangReleaseprocessProgressBar from '@/components/vorgaenge/vorgang-releaseprocess-progress-bar.vue';
import TeilnehmerPersonList from './teilnehmer-person-list.vue';
import TeilnehmerDetail from './teilnehmer-detail.vue';
import TeilnehmerVerlauf from './teilnehmer-verlauf.vue';
import DeleteConfirmationModal from '@/components/common/delete-confirmation-modal.vue';
import { ChevronRight, ChevronDown, Hash, Calendar, Users, Check } from 'lucide-vue';

export default {
  name: 'TeilnehmerVorgangCard',
  components: {
    VorgangReleaseprocessProgressBar,
    TeilnehmerPersonList,
    TeilnehmerDetail,
    TeilnehmerVerlauf,
    DeleteConfirmationModal,
    ChevronRight,
    ChevronDown,
    Hash,
    Calendar,
    Users,
    Check,
  },
  props: {
    vorgang: { type: Object, required: true },
    konfiguration: { type: Object },
    reiseterminStartdatum: { type: [String, Object] },
    reiseterminEnddatum: { type: [String, Object] },
    isReiseOhneFlug: { type: Boolean, default: false },
    isEditAllowed: { type: Boolean, default: false },
    dokumenteByTeilnehmer: { type: Object, default: () => ({}) },
    saveHandler: { type: Function, default: null },
    showReleasepruefung: { type: Boolean, default: true },
    verlaufEnabled: { type: Boolean, default: true },
    downloadAllowed: { type: Boolean, default: true },
    expanded: { type: Boolean, default: false },
    // Steuern ausschließlich Sichtbarkeit und Bedienbarkeit der Prüf-Checkbox im Kartenkopf
    isReiseterminInReleasePruefung: { type: Boolean, default: false },
    passdatenCheckRequired: { type: Boolean, default: false },
    dokumenteCheckRequired: { type: Boolean, default: false },
    reiseterminState: { type: String, default: null },
  },
  data() {
    return {
      selectedTeilnehmerIndex: 0,
      showVerlauf: false,
      hasUnsavedChanges: false,
      pendingVerlassen: null,
    };
  },
  computed: {
    teilnehmerListe() {
      return this.vorgang.teilnehmer || [];
    },
    selectedTeilnehmer() {
      return this.teilnehmerListe[this.selectedTeilnehmerIndex] || this.teilnehmerListe[0] || null;
    },
    // Checkbox erscheint nur in der Releaseprüfung, wenn laut Konfiguration
    // überhaupt Passdaten oder Dokumente geprüft werden müssen
    showGeprueftCheckbox() {
      return this.showReleasepruefung && (this.passdatenCheckRequired || this.dokumenteCheckRequired);
    },
    // Angehakt ist die Checkbox nur, wenn Passdaten und Dokumente geprüft sind
    istGeprueft() {
      const istGeprueftWert = wert => wert === 'GeprueftVorhandenKorrekt';
      return (
        (istGeprueftWert(this.vorgang.passdatenGeprueft) || this.konfiguration.passdaten === false) &&
        (istGeprueftWert(this.vorgang.dokumenteGeprueft) || this.konfiguration.dokumente === false)
      );
    },
    // Die Prüfung ist nur mit Teilnehmerdaten-Vollzugriff und bei aufgelegtem Reisetermin
    // während der Releaseprüfung möglich,aber nicht bei unvollständigen Teilnehmerdaten
    isGeprueftDisabled() {
      const teilnehmerdatenStatus = this.vorgang.teilnehmerdatenStatus;
      return (
        !this.isEditAllowed ||
        this.reiseterminState !== 'Aufgelegt' ||
        !this.isReiseterminInReleasePruefung ||
        !['Buchung', 'StornierteBuchung'].includes(this.vorgang.blankState) ||
        (teilnehmerdatenStatus !== 'AllGood' && teilnehmerdatenStatus !== 'CheckRequired')
      );
    },
    // Je Vorgang eindeutige Modal-Id, da mehrere Karten gleichzeitig geöffnet sein können
    verwerfenModalId() {
      return `teilnehmerVerwerfenModal-${this.vorgang.id}`;
    },
  },
  watch: {
    teilnehmerListe() {
      if (this.selectedTeilnehmerIndex >= this.teilnehmerListe.length) {
        this.selectedTeilnehmerIndex = 0;
      }
    },
  },
  methods: {
    darfVerlassen() {
      if (!this.hasUnsavedChanges) return Promise.resolve(true);
      return new Promise(resolve => {
        this.pendingVerlassen = resolve;
        this.$bvModal.show(this.verwerfenModalId);
      });
    },
    aufloesen(darfVerlassen) {
      const resolve = this.pendingVerlassen;
      this.pendingVerlassen = null;
      if (resolve) resolve(darfVerlassen);
    },
    onVerwerfenConfirm() {
      this.hasUnsavedChanges = false;
      this.aufloesen(true);
      this.$bvModal.hide(this.verwerfenModalId);
    },
    onVerwerfenCancel() {
      this.aufloesen(false);
    },
    async toggleExpanded() {
      if (this.expanded && !(await this.darfVerlassen())) return;
      this.$emit('update:expanded', !this.expanded);
    },
    async onSelectTeilnehmer(index) {
      if (index === this.selectedTeilnehmerIndex && !this.showVerlauf) return;
      if (!(await this.darfVerlassen())) return;
      this.selectedTeilnehmerIndex = index;
      this.showVerlauf = false;
    },
    async onShowVerlauf(teilnehmer) {
      if (!(await this.darfVerlassen())) return;
      const index = this.teilnehmerListe.indexOf(teilnehmer);
      if (index !== -1 && index !== this.selectedTeilnehmerIndex) {
        this.selectedTeilnehmerIndex = index;
        this.showVerlauf = true;
        return;
      }
      this.showVerlauf = !this.showVerlauf;
    },
  },
};
</script>

<style lang="scss" scoped>
.teilnehmer-vorgang-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  margin-bottom: 12px;
}
.teilnehmer-vorgang-card__header {
  display: grid;
  grid-template-columns: 300px repeat(3, 1fr);
  align-items: center;
  padding: 16px 0;
  cursor: pointer;

  &:hover {
    background-color: #f9fafb;
  }
}
.teilnehmer-vorgang-card__header-group {
  display: flex;
  align-items: center;
  min-width: 0;

  // Innerhalb der Gruppe schließt die BNR ohne Trennstrich und Spaltenabstand direkt ans Chevron an
  .teilnehmer-vorgang-card__header-item--bnr {
    padding-left: 0;

    &::before {
      display: none;
    }
  }
}
.teilnehmer-vorgang-card__header-item {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;
  padding-left: 24px;

  &:not(:first-child)::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 1px;
    height: 30px;
    background-color: #e5e7eb;
  }

  &:first-child {
    padding-left: 20px;
  }

  &--release {
    padding-right: 20px;
  }
}
.teilnehmer-vorgang-card__geprueft {
  display: flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
}
.teilnehmer-vorgang-card__geprueft-label {
  color: var(--text-muted);
  font-family: Poppins, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0;
  text-transform: uppercase;
  margin-right: 8px;

  .teilnehmer-vorgang-card__geprueft:disabled & {
    color: var(--text-subtle);
  }
}
// Quadratische Checkbox
.teilnehmer-vorgang-card__geprueft-box {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border: 1px solid var(--border-strong);
  border-radius: 4px;
  background: var(--surface);
  color: var(--surface);

  .teilnehmer-vorgang-card__geprueft:not(:disabled):hover & {
    border-color: var(--success);
  }

  &--checked {
    background: var(--success);
    border-color: var(--success);
  }
}
.teilnehmer-vorgang-card__header-label {
  color: #6b7280;
  font-family: Poppins, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0;
  text-transform: uppercase;
  margin-right: 16px;
}
.teilnehmer-vorgang-card__header-value {
  color: #0a0a0a;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 16px;
  letter-spacing: 0;
}
.teilnehmer-vorgang-card__body {
  border-top: 1px solid #e5e7eb;
  background-color: #fff;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}
.teilnehmer-vorgang-card__sidebar {
  width: 300px;
  flex-shrink: 0;
  border-right: 1px solid #ebedf3;
}
.teilnehmer-vorgang-card__detail {
  margin: 16px 24px;
  background-color: #fbfcfd;
}
</style>
